<template>
  <v-container fluid>
    <v-card class="card-datatable">
      <v-card-title>
        <v-row align="center" justify="space-between" no-gutters>
          <v-col cols="auto" class="d-none d-md-block">
            <span class="headline no-split-words">
              {{ $t($route.meta.label) }}
            </span>
          </v-col>
          <v-col class="text-right">
            <debounced-text-field
              append-icon="search"
              class="d-md-inline-block"
              dense
              hide-details
              v-model="search"
              :label="$t('search')"
              @input="onSearchChange"
            ></debounced-text-field>
            <v-btn
              color="success ml-2"
              :to="{
                name: 'Create Basic Profile',
                query: { role: 'ROLE_ADMIN' },
                params: { backPrevious: true },
              }"
            >
              <v-icon>add</v-icon>
              <span class="d-none d-sm-block"> {{ $t("actions.new") }} </span>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-data-table
              class="rows-clickable"
              :footer-props="tableFooterProps"
              :headers="headers"
              :items="items"
              :loading="isLoading"
              :options="pagination"
              :server-items-length="totalItems"
              @update:options="onPaginationChange"
              @click:row="entityDetail"
            >
              <template v-slot:[`item.dateStart`]="{ item }">
                <span>
                  {{ item.dateStart | dateTimeWithTz }}
                </span>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="primary"
                      @click.stop="entityDetail(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon color="primary"> description </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("actions.detail") }}</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="currentUserId !== item.userId"
                      icon
                      color="red"
                      @click.stop="selectDeactivateUser(item)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("actions.delete") }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Dialogo para la eliminacion de usuarios -->
    <delete-dialog
      :dialog="deleteDialog"
      :loading="deleteLoading"
      @cancel="deleteDialog = false"
      @submit="deactivateUser"
    ></delete-dialog>
  </v-container>
</template>

<script>
import tableFooterProps from "@/common/table-footer-props";
import defaultPaginationSettings from "@/common/default-pagination-settings";
import DebouncedTextField from "@/components/debouncing-inputs/DebouncedTextField";
import DeleteDialog from "@/components/modal_dialog/DeleteDialog";
import RepositoryFactory from "@/repositories/RepositoryFactory";
import auth from "@/common/auth";
import {
  generateSort,
  parseStringToSortBy,
  parseStringToSortDesc,
} from "@/common/pagination-utils";

const UserDataEntityRepository = RepositoryFactory.get(
  "UserDataEntityRepository"
);

export default {
  name: "AdminList",
  components: {
    DebouncedTextField,
    DeleteDialog,
  },
  data() {
    return {
      items: [],
      deleteLoading: false,
      isLoading: false,
      totalItems: 0,
      pagination: {
        page:
          parseInt(this.$route.query.page) || defaultPaginationSettings.page,
        itemsPerPage:
          parseInt(this.$route.query.pageSize) ||
          defaultPaginationSettings.itemsPerPage,
        sortBy: parseStringToSortBy(this.$route.query.sort),
        sortDesc: parseStringToSortDesc(this.$route.query.sort),
      },
      search: this.$route.query.search,
      selectedEntity: null,
      deleteDialog: false,
      currentUserId: auth.getUser().id,
      tableFooterProps,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t("profile.fields.display_name"),
          value: "displayName",
        },
        {
          text: this.$t("profile.fields.surname"),
          value: "surname",
        },
        {
          text: this.$t("profile.fields.name"),
          value: "name",
        },
        {
          text: this.$t("profile.fields.main_email"),
          value: "mainEmail",
        },
        {
          text: this.$t("profile.fields.main_phone"),
          value: "mainPhone",
        },
        {
          text: this.$t("profile.fields.date_start"),
          value: "dateStart",
        },
        { text: "", sortable: false, value: "action" },
      ];
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.isLoading = true;
      const sortMapping = {
        name: "user.firstName",
        surname: "user.lastName",
        mainEmail: "user.email",
      };
      const options = {
        params: {
          page: this.pagination.page - 1,
          authority: "ROLE_ADMIN",
          size: this.pagination.itemsPerPage,
          sort: generateSort(this.pagination, sortMapping),
          search: this.search ? this.search : undefined,
        },
      };
      UserDataEntityRepository.getAll(options)
        .then((response) => {
          this.items = response.content;
          this.totalItems = response.totalElements;
        })
        .catch(() =>
          this.$log.debug("Error fetching admin list with params: " + options)
        )
        .finally(() => (this.isLoading = false));
    },
    entityDetail(entity) {
      const selection = window.getSelection().toString();
      if (selection.length === 0) {
        this.$router.push({
          name: "AdminView Detail",
          params: { id: entity.userId, backPrevious: true },
        });
      }
    },
    redirect(query) {
      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        this.$router.replace({
          name: this.$route.name,
          query: query,
        });
        this.fetchData();
      }
    },
    onPaginationChange(pagination = this.pagination) {
      this.pagination = pagination;
      let query = JSON.parse(JSON.stringify(this.$route.query));
      query.page = this.pagination.page.toString();
      query.pageSize = this.pagination.itemsPerPage.toString();
      query.search = this.search ? this.search : undefined;
      query.sort = generateSort(this.pagination);
      this.redirect(query);
    },
    onSearchChange() {
      if (this.pagination.page !== 1) {
        this.pagination.page = 1;
      } else {
        this.onPaginationChange();
      }
    },
    deactivateUser() {
      this.deleteLoading = true;
      UserDataEntityRepository.deactivateUser(this.selectedEntity)
        .then(() => {
          this.items.splice(this.items.indexOf(this.selectedEntity), 1);
        })
        .catch(() =>
          this.$log.debug("Error deactivating user: " + this.selectedEntity)
        )
        .finally(() => {
          this.selectedEntity = null;
          this.deleteDialog = false;
          this.deleteLoading = false;
        });
    },
    selectDeactivateUser(entity) {
      this.selectedEntity = entity;
      this.deleteDialog = true;
    },
  },
};
</script>
